import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { OpenApiRequestStatus, useOpenApiRequest } from "typescript-request-utils";
import styled from "styled-components";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";

import { Application } from "../api/models/Application";
import { EntityQueryResults } from "../api/models/EntityQueryResults";
import { Organization } from "../api/models/Organization";
import { getCurrentUser, getWindowWidth, setEndingAuthorizedRepresentations, setEndingRepresentations, setPendingReceivedApplicationsCount } from "../features/basic/reducers/basicReducer";
import PageSelection from "../framework/components/paging/PageSelection";
import PageSizeSelection from "../framework/components/paging/PageSizeSelection";
import Search from "../framework/components/search/Search";
import { FilterContainer, ViewDiv } from "../framework/components/view/viewComponents";
import { authorizedUsersTableColumns, customerAuthorizationsTableColumns, organizationTableColumns, receivedApplicationsTableColumns, sentApplicationsTableColumns } from "../framework/configuration/tableColumns";
import { getApiRegistry } from "../framework/state/apiRegistry";
import { ViewType } from "./enums/ViewType";
import Table from "../framework/components/view/Table";
import { SearchFilterItem } from "../framework/components/search/SearchFilterItem";
import { UserCustomer } from "../api/models/UserCustomer";
import NewApplicationModal from "../framework/components/modal/NewApplicationModal";
import { MobileQuestionMarkIcon, PlusIcon } from "../framework/components/table/rowIcons";
import { useHistory } from "react-router-dom";
import { CheckmarkIcon } from "../framework/components/modal/modalIcons";
import { useTheme } from "../features/context/CustomerContext";
import useCustomTranslation from "../features/hooks/CustomUseTranslation";
import { requestHasEndingRepresenattions } from "../utilities/RepresentationUtil";
import { useAuth } from "react-oidc-context";

export const NewApplicationButtonWrapper = styled.div`
    position: relative;
`
export const MobileContainer = styled.div`
    display: flex;
`
export const NewApplicationButton = styled.div`
    width: 55px;
    color: ${props => props.theme.brandColor1};
    background-color: ${props => props.theme.brandColor1};
    height: 30px;
    border-radius: 100px;
    margin-left: 5px;
    cursor: pointer;
`
const QuestionMark = styled.div`
    width: 30px;
    color: ${props => props.theme.brandColor1};
    background-color: ${props => props.theme.brandColor1};
    height: 30px;
    border-radius: 100px;
    margin: 20px;
    text-align: center;
    cursor: pointer;
    position: absolute;
    top: 0;
    left: 0;

`
const ToastTextContainer = styled.div`
    @media screen and (max-width: 1440px) {
        width: 150px;
        left: 50px;
        height: 50px;
        bottom: 40px;
    }
    bottom: 20px;
    left: 57px;
    position: relative;
    width: 400px;
`
const EnterpriseToastTextContainer = styled.div`
    @media screen and (max-width: 1440px) {
        width: 150px;
        bottom: 80px;
        left: 50px;
        padding-top: 20px;
        height: 160px;
    }
    left: 57px;
    position: relative;
    width: 400px;
    height: 140px;
    bottom: 40px;
`
const ToastMessage = styled.p`
    font-weight: 400;
    margin-bottom: 10px;
    height: auto !important;
    margin-left: 5px;
`
export interface TableViewProps {
    type: ViewType;
    searchFilters: SearchFilterItem[];
    showInfo: boolean;
}

const TableView: FC<TableViewProps> = (props) => {
    const auth = useAuth();
    const { t } = useCustomTranslation();
    const dispatch = useDispatch();

    const[applications, setApplications] = useState<EntityQueryResults<Application> | undefined>();
    const[pendingReceivedApplicationsCountRequest, loadApplications] = useOpenApiRequest(getApiRegistry().applicationsApi.getRepresentationApplications);

    const[organizations, setOrganizations] = useState<EntityQueryResults<Organization>>();
    const[organizationsRequest, loadOrganizations] = useOpenApiRequest(getApiRegistry().organizationsApi.getOrganizations);
  
    const[customerAuthorizations, setCustomerAuthorizations] = useState<EntityQueryResults<UserCustomer> | undefined>();
    const[customerAuthorizationsRequest, loadCustomerAuthorizations] = useOpenApiRequest(getApiRegistry().userCustomerApi.getUserCustomers);

    const[authorizedUsers, setAuthorizedUsers] = useState<EntityQueryResults<UserCustomer> | undefined >();
    const[authorizedUsersRequest, loadAuthorizedUsers] = useOpenApiRequest(getApiRegistry().userCustomerApi.getAuthorizedUsers);
    
    const [pageSize, setPageSize] = useState<number>(20);
    const [pageNumber, setPageNumber] = useState<number>(getPageNumber());
    const [totalPages, setTotalPages] = useState<number>(1);
  
    const [sortOrder, setSortOrder] = useState<string>("asc");
    const [sortColumn, setSortColumn] = useState<string>("");
  
    const[dateFilter, setDateFilter] = useState<boolean>(false);
    const [filter, setFilter] = useState<string>(props.searchFilters[0] ? props.searchFilters[0].filterName : "");
    const [filterParam, setFilterParam] = useState<string | Date>("");

    const [showModal, setShowModal] = useState<boolean>(false);
    const [showInfoText, setShowInfoText] = useState<boolean>(props.showInfo);
    const [showToast, setShowToast] = useState<boolean>((localStorage.getItem('showToast') === 'personalApplication' || localStorage.getItem('showToast') === 'enterpriseApplication') ? true : false);

    const currentUser = useSelector(getCurrentUser);
    const windowWidth = useSelector(getWindowWidth);
    const history = useHistory();

    const theme = useTheme();

    const showToastMessage = () => {
        toast.success(
        <div>
            <CheckmarkIcon />
            {localStorage.getItem('showToast') === 'personalApplication' &&
            <ToastTextContainer>
                <ToastMessage>{t('ApplicationSuccessToastFirst')}</ToastMessage>
                <ToastMessage>{t('ApplicationSuccessToastSecond')}</ToastMessage>
            </ToastTextContainer>
            }
            {localStorage.getItem('showToast') === 'enterpriseApplication' &&
            <EnterpriseToastTextContainer>
                <ToastMessage>{t('EnterpriseApplicationSuccessToastFirst')}</ToastMessage>
                <ToastMessage>{t('EnterpriseApplicationSuccessToastSecond')}</ToastMessage>
                <ToastMessage>{t('EnterpriseApplicationSuccessToastThird')}</ToastMessage>
            </EnterpriseToastTextContainer>
            }
        </div>, {
            className: localStorage.getItem('showToast') === 'personalApplication' ? "Toastify__toast-container--personal" : "Toastify__toast-container" ,
            position: toast.POSITION.TOP_RIGHT,
            icon: false,
            hideProgressBar: true,
            autoClose: 15000
        });
    };

    if(showInfoText){
        localStorage.setItem('infoTextAlreadyDisplayed', 'true');
    }
    useEffect(() => {
        if(showToast){
            showToastMessage();
            localStorage.setItem('showToast', '');
            setShowToast(false);
        }
        // eslint-disable-next-line
    }, [showToast]);

    useEffect(() => {
        loadEntities();
        // eslint-disable-next-line
    }, [pageSize, pageNumber, sortOrder, sortColumn, filter, filterParam, currentUser, props.type]);

    useEffect(() => {
        if (organizationsRequest.type === OpenApiRequestStatus.Complete) {
            setOrganizations(organizationsRequest.responsePayload);
            setTotalPages(organizationsRequest.responsePayload.totalPages);
        }
        else if (organizationsRequest.type === OpenApiRequestStatus.NetworkError || organizationsRequest.type === OpenApiRequestStatus.ServerError) {
            setOrganizations(undefined);
            setTotalPages(0);
        }
    }, [organizationsRequest]);

    useEffect(() => {
        if (pendingReceivedApplicationsCountRequest.type === OpenApiRequestStatus.Complete) {
            setApplications(pendingReceivedApplicationsCountRequest.responsePayload);
            setTotalPages(pendingReceivedApplicationsCountRequest.responsePayload.totalPages);
            if (props.type === ViewType.RECEIVED_APPLICATIONS) {
                dispatch(setPendingReceivedApplicationsCount(pendingReceivedApplicationsCountRequest.responsePayload?.totalEntities))
            }
        }
        else if (pendingReceivedApplicationsCountRequest.type === OpenApiRequestStatus.NetworkError || pendingReceivedApplicationsCountRequest.type === OpenApiRequestStatus.ServerError) {
            setApplications(undefined);
            setTotalPages(0);
        }
    // eslint-disable-next-line
    }, [pendingReceivedApplicationsCountRequest]);

    useEffect(() => {
        if (customerAuthorizationsRequest.type === OpenApiRequestStatus.Complete) {
            setCustomerAuthorizations(customerAuthorizationsRequest.responsePayload);
            setTotalPages(customerAuthorizationsRequest.responsePayload.totalPages);
            if(props.type === ViewType.CUSTOMER_AUTHORIZATIONS) {
                dispatch(setEndingRepresentations(requestHasEndingRepresenattions(customerAuthorizationsRequest.responsePayload)));
            }
        }
        else if (customerAuthorizationsRequest.type === OpenApiRequestStatus.NetworkError || customerAuthorizationsRequest.type === OpenApiRequestStatus.ServerError) {
            setCustomerAuthorizations(undefined);
            setTotalPages(0);
        }
        // eslint-disable-next-line
    }, [customerAuthorizationsRequest]);

    useEffect(() => {
        if (authorizedUsersRequest.type === OpenApiRequestStatus.Complete) {
            setAuthorizedUsers(authorizedUsersRequest.responsePayload);
            setTotalPages(authorizedUsersRequest.responsePayload.totalPages);
            if(props.type === ViewType.AUTHORIZED_USERS) {
                dispatch(setEndingAuthorizedRepresentations(requestHasEndingRepresenattions(authorizedUsersRequest.responsePayload)));
            }
        }
        else if (authorizedUsersRequest.type === OpenApiRequestStatus.NetworkError || authorizedUsersRequest.type === OpenApiRequestStatus.ServerError) {
            setAuthorizedUsers(undefined);
            setTotalPages(0);
        }
    // eslint-disable-next-line
    }, [authorizedUsersRequest]);

    useEffect(() => {
        //Set default state when view type changes
        setShowInfoText(props.showInfo);
        setApplications(undefined);
        setPageSize(20);
        setPageNumber(1);
        setSortOrder("asc");
        setSortColumn("");
        setFilter(props.searchFilters[0] ? props.searchFilters[0].filterName : "");
        setFilterParam("");
    // eslint-disable-next-line
    }, [props.type])
  
    function loadEntities() {
        const sort = sortColumn ? sortColumn + ' ' + sortOrder : '';
        if(props.type === ViewType.ORGANIZATIONS){
            loadOrganizations({filter, sort, filterParam, pageNumber, pageSize});
        } else if((props.type === ViewType.RECEIVED_APPLICATIONS || props.type === ViewType.SENT_APPLICATIONS)){
            loadApplications({pageNumber, pageSize, sort, filter, filterParam, type: props.type});
        } else if(props.type === ViewType.CUSTOMER_AUTHORIZATIONS && currentUser?.username){
            loadCustomerAuthorizations({pageNumber, pageSize, sort, filter, filterParam, usernames: currentUser?.username});
        } else if(props.type === ViewType.AUTHORIZED_USERS){
            loadAuthorizedUsers({filter, sort, filterParam, pageNumber, pageSize});
        }
    }

    function handleSort(sortParam: string) {
      if (sortParam === null || sortParam === undefined || sortParam === '') {
        return;
      }
      //We always want the first sort on column to be 'asc'
      if (sortColumn !== sortParam || sortOrder === "desc") {
        setSortOrder("asc");
      } else {
        setSortOrder("desc");
      }
      setSortColumn(sortParam);
    }
  
    function handleFilterChange(filter: string) {
        setFilter(filter);
        if (filter === "receivedStartDate" || filter === "receivedEndDate") {
          setFilterParam('');
          setDateFilter(true);
        }
        else if (dateFilter){
          setFilterParam('');
          setDateFilter(false);
        }
      }
  
    function handlePageSizeChange(newPageSize: number) {
      setPageSize(newPageSize);
      setPageNumber(1);
    }

    function getPageNumber() {
        if(ViewType.ORGANIZATIONS && organizations){
            return organizations.currentPage;
        } else if((ViewType.SENT_APPLICATIONS || ViewType.RECEIVED_APPLICATIONS) && applications){
            return applications.currentPage;
        } else if(ViewType.CUSTOMER_AUTHORIZATIONS && customerAuthorizations){
            return customerAuthorizations.currentPage;
        } else {
            return 1;
        }
    }

    function handleSave() {
        setShowModal(false);
        loadEntities();
    }

    const routeChange = () =>{ 
        let path = 'selectApplicationType'; 
        history.push(path);
      }

      return (
        auth.isAuthenticated ?
        <ViewDiv theme={theme}>
            <ToastContainer /> 
            <FilterContainer theme={theme}>
                {windowWidth > theme.mobileTableBreakPoint && <PageSizeSelection pageSize={pageSize} pageSizeChange={handlePageSizeChange} />}
                <MobileContainer>
                    {windowWidth < theme.mobileTableBreakPoint && 
                    <QuestionMark theme={theme}  onClick={() => {setShowInfoText(!showInfoText)}}>
                        <MobileQuestionMarkIcon />
                    </QuestionMark>}
                    <Search 
                        filter={filter} 
                        filterParam={filterParam instanceof Date ? moment(filterParam).format('yyyy-MM-DDTHH:mm:ss+SSSS') : filterParam} 
                        selectedDate={filterParam instanceof Date ? filterParam : undefined}
                        searchFilters={props.searchFilters} 
                        filterParamChange={setFilterParam} 
                        filterChange={handleFilterChange} />
                    {windowWidth <= theme.mobileTableBreakPoint && props.type === ViewType.SENT_APPLICATIONS && <>
                        <NewApplicationButtonWrapper >
                            <NewApplicationButton theme={theme} onClick={() => routeChange()}/>
                            <PlusIcon />
                        </NewApplicationButtonWrapper>
                        <NewApplicationModal show={showModal} afterSave={handleSave} /></>}
                </MobileContainer>
                <PageSelection pageNumber={pageNumber} totalPages={totalPages} changePage={setPageNumber} />
            </FilterContainer>

            {props.type === ViewType.ORGANIZATIONS && 
                <Table 
                    tableColumns={organizationTableColumns}
                    activateSort={handleSort}
                    isError={organizationsRequest.type === OpenApiRequestStatus.NetworkError || organizationsRequest.type === OpenApiRequestStatus.ServerError}
                    entities={organizations ? organizations.entities : []}
                    onUpdate={loadEntities}
                    type={ViewType.ORGANIZATIONS}
                    errorMessage={t("Error on loading organizations")}
                    infoMessage={t("InfoTextOrganizations")}
                    showInfoText={showInfoText}
                    setShowInfoText={setShowInfoText}
                    filterUsed={filterParam}/>}

            {props.type === ViewType.RECEIVED_APPLICATIONS &&
                <Table 
                    tableColumns= {receivedApplicationsTableColumns}
                    activateSort={handleSort}
                    isError={pendingReceivedApplicationsCountRequest.type === OpenApiRequestStatus.NetworkError || pendingReceivedApplicationsCountRequest.type === OpenApiRequestStatus.ServerError}
                    entities={applications ? applications.entities : []}
                    type={props.type}
                    onUpdate={loadEntities}
                    errorMessage={t("Error on loading applications")}
                    infoMessage= {t("InfoTextReceivedApplications")}
                    showInfoText={showInfoText}
                    setShowInfoText={setShowInfoText}
                    filterUsed={filterParam}/>}

            {props.type === ViewType.SENT_APPLICATIONS &&
                <Table 
                    tableColumns={sentApplicationsTableColumns}
                    activateSort={handleSort}
                    isError={pendingReceivedApplicationsCountRequest.type === OpenApiRequestStatus.NetworkError || pendingReceivedApplicationsCountRequest.type === OpenApiRequestStatus.ServerError}
                    entities={applications ? applications.entities : []}
                    type={props.type}
                    onUpdate={loadEntities}
                    errorMessage={t("Error on loading applications")}
                    infoMessage= {t("InfoTextSentApplications")}
                    showInfoText={showInfoText}
                    setShowInfoText={setShowInfoText}
                    filterUsed={filterParam}/>}

            {props.type === ViewType.CUSTOMER_AUTHORIZATIONS &&
                <Table tableColumns={customerAuthorizationsTableColumns}
                   activateSort={handleSort}
                   isError={customerAuthorizationsRequest.type === OpenApiRequestStatus.NetworkError || customerAuthorizationsRequest.type === OpenApiRequestStatus.ServerError}
                   entities={customerAuthorizations ? customerAuthorizations.entities : []}
                   onUpdate={loadEntities}
                   type={ViewType.CUSTOMER_AUTHORIZATIONS}
                   errorMessage={t("Error on loading customers")}
                   infoMessage={t("InfoTextCustomerAuthorizations")}
                   showInfoText={showInfoText}
                   setShowInfoText={setShowInfoText}
                   filterUsed={filterParam}/>}

            {props.type === ViewType.AUTHORIZED_USERS &&
                <Table tableColumns={authorizedUsersTableColumns}
                activateSort={handleSort}
                isError={authorizedUsersRequest.type === OpenApiRequestStatus.NetworkError || authorizedUsersRequest.type === OpenApiRequestStatus.ServerError}
                entities={authorizedUsers ? authorizedUsers.entities : []}
                onUpdate={loadEntities}
                type={ViewType.AUTHORIZED_USERS}
                errorMessage={t("Error on loading authorized users")}
                infoMessage={t("InfoTextAuthorizedUsers")}
                showInfoText={showInfoText}
                setShowInfoText={setShowInfoText}
                filterUsed={filterParam}/> }  
      </ViewDiv>
      : <ViewDiv theme={theme}>{t("Unauthorized")}</ViewDiv>
    )
}

export default TableView;